import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Spinner, Disclaimer } from "@/components/shared";
import { Textarea } from "@/components/ui/textarea";
import { aaLabel, AA_CATEGORIES, AA_STATUS_STYLE, AA_CRISIS } from "@/lib/askAnything";
import { ArrowLeft, Send, MessageCircleHeart, Paperclip, CheckCircle2, ShieldAlert, ListPlus, BellPlus, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const API_BASE = process.env.REACT_APP_BACKEND_URL;

function Bubble({ m, threadId }) {
  const isUser = m.sender_type === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`aa-msg-${m.sender_type}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${isUser ? "bg-kid-teal text-white" : "bg-kid-mist text-kid-ink border border-kid-line"}`}>
        {!isUser && <div className="text-[.7rem] font-bold uppercase tracking-wide text-kid-teal mb-1 flex items-center gap-1"><MessageCircleHeart size={12} /> KidPath</div>}
        {m.body}
        {(m.attachments || []).map((a, i) => (
          <a key={i} href={`${API_BASE}/api/ask-anything/threads/${threadId}/attachment?key=${encodeURIComponent(a.storage_key)}`}
            target="_blank" rel="noreferrer" className={`mt-2 flex items-center gap-1.5 text-xs font-semibold underline ${isUser ? "text-white/90" : "text-kid-teal"}`} data-testid="aa-msg-attachment">
            <Paperclip size={12} /> {a.file_name}
          </a>
        ))}
        <div className={`text-[.65rem] mt-1.5 ${isUser ? "text-white/70" : "text-kid-muted"}`}>{fmt(m.created_at)}</div>
      </div>
    </div>
  );
}

export default function AskThread() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [feedback, setFeedback] = useState({});
  const [savingMsg, setSavingMsg] = useState("");
  const endRef = useRef(null);

  const load = async () => {
    try {
      setData((await api.get(`/ask-anything/threads/${id}`)).data);
      const { data: fb } = await api.get(`/ask-anything/threads/${id}/feedback`);
      setFeedback(fb.feedback || {});
    }
    catch { toast.error("Conversation not found."); navigate("/app/ask"); }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const saveTask = async (mid, withReminder) => {
    setSavingMsg(mid + (withReminder ? "-r" : "-t"));
    try {
      const due = withReminder ? new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10) : undefined;
      await api.post(`/ask-anything/threads/${id}/save-task`, { message_id: mid, due_date: due });
      toast.success(withReminder ? "Reminder added to your tasks (in 1 week)." : "Saved to your roadmap tasks.",
        { action: { label: "View", onClick: () => navigate("/app/tasks") } });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingMsg(""); }
  };
  const sendFeedback = async (mid, helpful) => {
    setFeedback((f) => ({ ...f, [mid]: helpful }));
    try { await api.post(`/ask-anything/threads/${id}/feedback`, { message_id: mid, helpful }); }
    catch { toast.error("Couldn't save feedback."); }
  };

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const { data: res } = await api.post(`/ask-anything/threads/${id}/messages`, { body: reply.trim() });
      setReply(""); await load();
      if (res?.safety_notice) toast.warning(res.safety_notice, { duration: 12000 });
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send."); }
    finally { setSending(false); }
  };
  const close = async () => {
    setClosing(true);
    try { await api.patch(`/ask-anything/threads/${id}/close`); await load(); toast.success("Conversation closed."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setClosing(false); }
  };

  if (!data) return <Spinner />;
  const { thread, messages, can_reply } = data;
  const isClosed = thread.status === "closed";

  return (
    <div className="max-w-3xl kid-stagger">
      <Link to="/app/ask" className="inline-flex items-center gap-1.5 text-sm text-kid-muted hover:text-kid-teal mb-4" data-testid="aa-back"><ArrowLeft size={16} /> All conversations</Link>

      {thread.safety_flag && (
        <div className="rounded-xl bg-kid-coral/10 border border-kid-coral/30 p-4 mb-4 flex items-start gap-3" data-testid="aa-crisis-banner">
          <ShieldAlert size={20} className="text-kid-coral shrink-0 mt-0.5" />
          <p className="text-sm text-kid-ink/85">{AA_CRISIS}</p>
        </div>
      )}

      <div className="kid-card p-5 mb-4" data-testid="aa-thread-header">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-lg font-extrabold text-kid-navy">{thread.title}</h1>
            <div className="flex items-center gap-2 mt-1.5">
              <span className={`text-[.65rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${AA_STATUS_STYLE[thread.user_status] || "bg-kid-mist text-kid-muted"}`}>{thread.user_status}</span>
              <span className="text-xs text-kid-muted">{aaLabel(AA_CATEGORIES, thread.category)}</span>
            </div>
          </div>
          {!isClosed && (
            <button onClick={close} disabled={closing} className="kid-btn kid-btn-ghost h-9 px-4 text-sm text-kid-muted" data-testid="aa-close-btn">
              <CheckCircle2 size={15} /> Close
            </button>
          )}
        </div>
        {thread.status !== "sent" && !isClosed && (
          <p className="text-xs text-kid-muted mt-3">We'll reply within 72 hours. Your message is with KidPath.</p>
        )}
      </div>

      <div className="space-y-3 mb-4" data-testid="aa-messages">
        {messages.map((m) => (
          <div key={m.id}>
            <Bubble m={m} threadId={id} />
            {m.sender_type === "admin" && (
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap justify-start" data-testid="aa-reply-actions">
                <button onClick={() => saveTask(m.id, false)} disabled={savingMsg === m.id + "-t"} className="inline-flex items-center gap-1 text-xs font-poppins font-semibold text-kid-muted hover:text-kid-teal px-2 py-1 rounded-lg hover:bg-kid-mist transition-colors" data-testid="aa-save-roadmap">
                  <ListPlus size={13} /> Save to roadmap
                </button>
                <button onClick={() => saveTask(m.id, true)} disabled={savingMsg === m.id + "-r"} className="inline-flex items-center gap-1 text-xs font-poppins font-semibold text-kid-muted hover:text-kid-teal px-2 py-1 rounded-lg hover:bg-kid-mist transition-colors" data-testid="aa-remind-me">
                  <BellPlus size={13} /> Remind me
                </button>
                <span className="w-px h-4 bg-kid-line mx-1" />
                <span className="text-xs text-kid-muted mr-0.5">Helpful?</span>
                <button onClick={() => sendFeedback(m.id, true)} className={`p-1.5 rounded-lg transition-colors ${feedback[m.id] === true ? "bg-kid-green/15 text-[#3f9d5c]" : "text-kid-muted hover:bg-kid-mist"}`} data-testid="aa-feedback-yes"><ThumbsUp size={13} /></button>
                <button onClick={() => sendFeedback(m.id, false)} className={`p-1.5 rounded-lg transition-colors ${feedback[m.id] === false ? "bg-kid-coral/15 text-kid-coral" : "text-kid-muted hover:bg-kid-mist"}`} data-testid="aa-feedback-no"><ThumbsDown size={13} /></button>
              </div>
            )}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {isClosed ? (
        <div className="kid-card p-5 text-center text-sm text-kid-muted" data-testid="aa-closed-note">
          This thread is closed. You can still read this conversation anytime. Start a new Ask Anything thread whenever something else comes up.
        </div>
      ) : can_reply ? (
        <div className="kid-card p-4" data-testid="aa-reply-box">
          <Textarea value={reply} onChange={(e) => setReply(e.target.value.slice(0, 5000))} rows={3} data-testid="aa-reply-input"
            placeholder="Write a reply…" className="rounded-xl resize-none border-0 focus-visible:ring-0 p-0" />
          <div className="flex justify-end mt-2">
            <button onClick={send} disabled={sending || !reply.trim()} className="kid-btn kid-btn-primary h-10 px-5 disabled:opacity-50" data-testid="aa-reply-send">
              <Send size={15} /> {sending ? "Sending…" : "Reply"}
            </button>
          </div>
        </div>
      ) : null}

      <div className="mt-6"><Disclaimer>{thread.disclaimer_block || "KidPath provides general information and organization support only. Please confirm important decisions with official sources and qualified professionals."}</Disclaimer></div>
    </div>
  );
}
